import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import type {
  TransformationOrder,
  TransformationRule,
  TransformationFilters,
  TransformationStats
} from '@/types/transformation.types';
import {
  transformationOrdersService,
  CreateTransformationOrderData
} from '@/services/transformationOrders.service';
import {
  mockTransformationOrders,
  mockTransformationRules,
  generateTransformationStats
} from '@/mocks/transformations.mock';

export const useTransformationOrders = (initialFilters?: Partial<TransformationFilters>) => {
  const [orders, setOrders] = useState<TransformationOrder[]>([]);
  const [rules, setRules] = useState<TransformationRule[]>([]);
  const [stats, setStats] = useState<TransformationStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load transformation orders from API
  const loadOrders = useCallback(async (filters?: Partial<TransformationFilters>) => {
    try {
      setIsLoading(true);
      setError(null);

      const params = {
        search: filters?.search || '',
        locationId: filters?.locationId || '',
        status: filters?.status || 'all',
        dateFrom: filters?.dateFrom || '',
        dateTo: filters?.dateTo || '',
        sortBy: 'createdAt',
        sortOrder: 'desc' as const,
        page: 1,
        limit: 100
      };

      const response = await transformationOrdersService.getTransformationOrders(params);

      if (response.success && response.data) {
        setOrders(response.data);
        // Generate stats based on loaded data
        setStats(generateTransformationStats(response.data));
      } else {
        throw new Error(response.error || 'Error al cargar las órdenes');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al cargar las órdenes de transformación';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error loading transformation orders:', err);
      // Fallback to mock data on error
      setOrders(mockTransformationOrders);
      setStats(generateTransformationStats());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOrders(initialFilters);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filterOrders = useCallback((filters: Partial<TransformationFilters>) => {
    loadOrders(filters);
  }, [loadOrders]);

  const createOrder = async (orderData: Omit<TransformationOrder, 'id' | 'orderNumber' | 'createdAt' | 'updatedAt'>) => {
    try {
      setIsLoading(true);
      setError(null);

      // Map the order data to the API format
      // Support both new multi-ingredient structure and old single-ingredient structure
      const createData: CreateTransformationOrderData = {
        transformationRuleId: orderData.transformationRuleId,
        locationId: orderData.locationId,
        notes: orderData.notes || '',
        scheduledDate: orderData.scheduledDate
      };

      // If new structure with sourceItems/targetItems arrays, use that
      if (orderData.sourceItems && orderData.sourceItems.length > 0) {
        createData.sourceItems = orderData.sourceItems.map(item => ({
          ingredientId: item.ingredientId || '',
          requestedQuantity: item.requestedQuantity || 0,
          unit: item.unit || 'kg'
        }));
      } else {
        // Fallback to old structure with single sourceQuantity
        createData.sourceQuantity = orderData.requestedSourceQuantity;
      }

      if (orderData.targetItems && orderData.targetItems.length > 0) {
        createData.targetItems = orderData.targetItems.map(item => ({
          ingredientId: item.ingredientId || '',
          expectedQuantity: item.expectedQuantity || 0,
          actualQuantity: item.actualQuantity || 0, // NEW: Include actual quantity for one-step completion
          unit: item.unit || 'kg'
        }));
      } else {
        // Fallback to old structure with single targetQuantity
        createData.targetQuantity = orderData.expectedTargetQuantity;
      }

      const response = await transformationOrdersService.createTransformationOrder(createData);

      if (response.success && response.data) {
        // Add the new order to the local state
        setOrders(prev => [...prev, response.data]);

        // Update stats
        setStats(generateTransformationStats([...orders, response.data]));

        toast.success(response.message || 'Orden de transformación creada exitosamente');

        return response.data;
      } else {
        throw new Error(response.error || 'Error al crear la orden');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Error al crear la orden de transformación';
      setError(errorMessage);
      toast.error(errorMessage);
      console.error('Error creating transformation order:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateOrderStatus = async (orderId: string, status: TransformationOrder['status'], notes?: string) => {
    try {
      setIsLoading(true);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedOrders = orders.map(order => 
        order.id === orderId 
          ? { 
              ...order, 
              status, 
              notes: notes || order.notes,
              updatedAt: new Date().toISOString(),
              ...(status === 'completed' && !order.completedAt ? { completedAt: new Date().toISOString() } : {})
            }
          : order
      );
      
      setOrders(updatedOrders);
      setStats(generateTransformationStats());
      
      return updatedOrders.find(order => order.id === orderId);
    } catch (err) {
      setError('Error al actualizar el estado de la orden');
      console.error('Error updating order status:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteOrder = async (orderId: string) => {
    try {
      setIsLoading(true);
      
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const updatedOrders = orders.filter(order => order.id !== orderId);
      setOrders(updatedOrders);
      setStats(generateTransformationStats());
      
      return true;
    } catch (err) {
      setError('Error al eliminar la orden de transformación');
      console.error('Error deleting transformation order:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const resetFilters = useCallback(() => {
    loadOrders();
  }, [loadOrders]);

  const refresh = useCallback(() => {
    loadOrders();
  }, [loadOrders]);

  return {
    orders,
    rules: mockTransformationRules, // Keep using mock rules for now
    stats,
    isLoading,
    error,
    filterOrders,
    createOrder,
    updateOrderStatus,
    deleteOrder,
    resetFilters,
    refresh
  };
};